/**
 * The planner's API (LIAA service plan S6 — P1-7): the planner of a range, the waiting list, free performers, an
 * order's bookings and tyre sets, booking / moving / taking off the plan, the caller's plan of the day.
 */

import { apiClient } from '@/shared/api/client';

export type BookingState = 'planned' | 'done' | 'cancelled';

export interface Booking {
  id: string;
  workOrderId: string;
  orderNumber: string;
  orderGroup: string;
  orderStatusId: string;
  urgent: boolean;
  workTypeId: string;
  customerName: string | null;
  vehicle: { id: string; plate: string | null; make: string | null; model: string | null } | null;
  resourceId: string | null;
  resourceName: string | null;
  staffId: string | null;
  staffName: string | null;
  startsAt: string;
  endsAt: string;
  state: BookingState;
  cancelReason: string | null;
  version: number;
  done: boolean;
  outsideHours: boolean;
}

export interface StaffPlan {
  id: string;
  name: string;
  userId: string | null;
  workTypeId: string | null;
  active: boolean;
  schedule: Array<{ weekday: number; startsAt: string; endsAt: string }>;
  absences: Array<{ id: string; kind: string; startsOn: string; endsOn: string }>;
}

export type WorkingHours = Record<string, [string, string] | null>;

export interface PlannerData {
  from: string;
  to: string;
  workingHours: WorkingHours;
  resources: Array<{ id: string; name: string; kind: string; active: boolean }>;
  staff: StaffPlan[];
  bookings: Booking[];
}

export interface WaitingOrder {
  workOrderId: string;
  number: string;
  urgent: boolean;
  workTypeId: string;
  statusId: string;
  group: string;
  customerName: string | null;
  vehicle: { id: string; plate: string | null; make: string | null; model: string | null };
  waitingSince: string;
}

export interface BookingConflict {
  reason: 'resource-busy' | 'staff-busy' | 'staff-absent' | 'staff-off-hours' | 'staff-inactive' | 'resource-inactive';
  bookingId?: string;
  orderNumber?: string;
  plate?: string | null;
  startsAt?: string;
  endsAt?: string;
  absenceKind?: string;
  hours?: { startsAt: string; endsAt: string } | null;
}

export interface PlannerTireSet {
  id: string;
  number: string;
  season: string;
  size: string;
  tireCount: number;
  locationName: string;
  path: string | null;
  /** Null (with the other billing fields) for a reader who does not see prices. */
  unpaidBalance: number | null;
  unpaidStage: string | null;
  withoutInvoice: string | null;
  oldestUnpaidDueOn: string | null;
}

export interface MyPlanItem {
  booking: Booking;
  works: Array<{ lineId: string; description: string; state: string; runningSince: string | null }>;
}

export interface BookingInput {
  resourceId: string | null;
  staffId: string | null;
  startsAt: string;
  endsAt: string;
}

const BASE = '/work-orders';

export const plannerKeys = {
  all: ['work-orders', 'planner'] as const,
  range: (from: string, to: string) => [...plannerKeys.all, 'range', from, to] as const,
  waiting: () => [...plannerKeys.all, 'waiting'] as const,
  free: (startsAt: string, endsAt: string, exclude: string | null) => [...plannerKeys.all, 'free', startsAt, endsAt, exclude] as const,
  order: (id: string) => [...plannerKeys.all, 'order', id] as const,
  tireSets: (id: string) => [...plannerKeys.all, 'tire-sets', id] as const,
  myPlan: (day: string | null) => [...plannerKeys.all, 'my-plan', day] as const,
};

export const fetchPlanner = async (from: string, to: string): Promise<PlannerData> =>
  (await apiClient.get<{ planner: PlannerData }>(`${BASE}/planner`, { params: { from, to } })).data.planner;

export const fetchWaiting = async (): Promise<WaitingOrder[]> => (await apiClient.get<{ orders: WaitingOrder[] }>(`${BASE}/planner/waiting`)).data.orders;

export const fetchFreeStaff = async (startsAt: string, endsAt: string, excludeBookingId: string | null): Promise<Array<{ id: string; name: string }>> =>
  (
    await apiClient.get<{ staff: Array<{ id: string; name: string }> }>(`${BASE}/planner/free-staff`, {
      params: { startsAt, endsAt, ...(excludeBookingId !== null && { excludeBookingId }) },
    })
  ).data.staff;

export const fetchOrderBookings = async (orderId: string): Promise<Booking[]> => (await apiClient.get<{ bookings: Booking[] }>(`${BASE}/${orderId}/bookings`)).data.bookings;

export const fetchOrderTireSets = async (orderId: string): Promise<{ tyreFitting: boolean; sets: PlannerTireSet[] }> =>
  (await apiClient.get<{ tyreFitting: boolean; sets: PlannerTireSet[] }>(`${BASE}/${orderId}/tire-sets`)).data;

export const createBooking = async (orderId: string, input: BookingInput): Promise<Booking> =>
  (await apiClient.post<{ booking: Booking }>(`${BASE}/${orderId}/bookings`, input)).data.booking;

export const updateBooking = async (bookingId: string, input: Partial<BookingInput> & { version: number }): Promise<Booking> =>
  (await apiClient.patch<{ booking: Booking }>(`${BASE}/bookings/${bookingId}`, input)).data.booking;

export const unplanOrder = async (orderId: string, reason: string): Promise<Booking[]> =>
  (await apiClient.post<{ bookings: Booking[] }>(`${BASE}/${orderId}/unplan`, { reason })).data.bookings;

export const fetchMyPlan = async (day: string | null): Promise<{ day: string; items: MyPlanItem[] }> =>
  (await apiClient.get<{ day: string; items: MyPlanItem[] }>(`${BASE}/my-plan`, { params: day === null ? {} : { date: day } })).data;

/**
 * A new order's tyre sets, before the order exists (S6): the tyre storage module's own list, by the customer and by the
 * car — for whoever reads tyre storage (it states the debt as that module does).
 */
export const fetchTireSetsOf = async (filter: { customerId: string | null; vehicleId: string | null }): Promise<PlannerTireSet[]> => {
  const one = async (params: Record<string, string>): Promise<PlannerTireSet[]> => {
    const { data } = await apiClient.get<{ data: Array<PlannerTireSet & { billing: { unpaidBalance: number; unpaidStage: string | null; withoutInvoice: string | null; oldestUnpaidDueOn: string | null } }> }>(
      '/tire-sets',
      { params: { view: 'active', limit: 50, ...params } },
    );
    return data.data.map((s) => ({
      id: s.id,
      number: s.number,
      season: s.season,
      size: s.size,
      tireCount: s.tireCount,
      locationName: s.locationName,
      path: s.path,
      unpaidBalance: s.billing.unpaidBalance,
      unpaidStage: s.billing.unpaidStage,
      withoutInvoice: s.billing.withoutInvoice,
      oldestUnpaidDueOn: s.billing.oldestUnpaidDueOn,
    }));
  };
  const lists = await Promise.all([
    filter.customerId === null ? [] : one({ customerId: filter.customerId }),
    filter.vehicleId === null ? [] : one({ vehicleId: filter.vehicleId }),
  ]);
  return lists.flat().filter((s, i, all) => all.findIndex((o) => o.id === s.id) === i);
};
